/**
 * Admin · connectors (GET /admin/connectors, PUT /admin/connectors/{typeKey}, owner-only writes).
 *
 * WHICH APPS CUSTOMERS CAN HAVE, AND WHICH CUSTOMERS. Two switches live on this screen; the
 * third belongs to the customer and is deliberately not reachable from here:
 *
 *   1. Offer a type to customers, or withdraw it from everybody. A VETO — nothing below can
 *      re-grant what this withdraws.
 *   2. Grant or deny ONE organisation, as an exception to that default. Clearing an exception
 *      returns the org to inheriting the platform answer, which is a distinct third state and
 *      the reason this isn't a plain on/off per org.
 *   3. (Not here.) Each org connects or disconnects its own instance on their Connections page.
 *      An administrator reaching into that switch would be indistinguishable from the customer's
 *      own choice — and the customer would simply switch it back.
 *
 * The type DEFINITIONS — fields, which are credentials, whether auth is needed — are contract in
 * the shared registry and are shown here read-only. This screen configures availability, not
 * schemas: a field the code cannot render, validate or store has no business being invented at
 * runtime, and that line is what keeps this from becoming a no-code integration builder the rest
 * of the architecture could not honour.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/AuthContext.js";
import { Info, Alert, Link2 } from "../../components/icons.js";
import { Switch, Modal, apiErrorText } from "./adminUi.js";

interface ConnectorTypeRow {
  key: string;
  label: string;
  description: string;
  category: string;
  provisioning: "native" | "external_subscription";
  auth: string;
  wired: boolean;
  singleton: boolean;
  prerequisite: string | null;
  requiresAuth: boolean;
  fieldCount: number;
  secretFieldCount: number;
  fields: { key: string; label: string; type: string; required?: boolean; secret?: boolean }[];
  enabled: boolean;
  notes: string | null;
  updatedAt: string | null;
  orgsConfigured: number;
  orgsActive: number;
  exceptions: number;
}

interface Exception {
  orgId: string;
  orgName: string;
  typeKey: string;
  typeLabel: string;
  enabled: boolean;
  notes: string | null;
  updatedAt: string;
}

interface AdminConnectorsResponse {
  types: ConnectorTypeRow[];
  exceptions: Exception[];
}

interface OrgRow {
  id: string;
  name: string;
}

const CATEGORY_LABEL: Record<string, string> = {
  intake: "Candidate intake",
  outbound: "Outbound mail",
  ats: "ATS & CRM",
  job_board: "Job boards",
};

export function Connectors(): ReactNode {
  const { me } = useAuth();
  const isOwner = me?.role === "owner";
  const queryClient = useQueryClient();

  const [inspecting, setInspecting] = useState<ConnectorTypeRow | null>(null);
  const [exceptionFor, setExceptionFor] = useState<ConnectorTypeRow | null>(null);
  const [exceptionOrg, setExceptionOrg] = useState("");
  const [exceptionEnabled, setExceptionEnabled] = useState(false);
  const [exceptionNotes, setExceptionNotes] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-connectors"],
    queryFn: () => api.get<AdminConnectorsResponse>("/admin/connectors"),
  });
  const isForbidden = error instanceof ApiError && error.status === 403;

  // Only loaded when the exception dialog is open — this screen is usually about types.
  const { data: orgsData } = useQuery({
    queryKey: ["admin-orgs-brief"],
    queryFn: () => api.get<{ orgs: OrgRow[] }>("/orgs"),
    enabled: exceptionFor !== null,
  });

  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: ["admin-connectors"] });

  const setAvailability = useMutation({
    mutationFn: (v: { key: string; enabled: boolean }) =>
      api.put(`/admin/connectors/${v.key}`, { enabled: v.enabled }),
    onSuccess: refresh,
  });

  const setException = useMutation({
    mutationFn: (v: { typeKey: string; orgId: string; enabled: boolean | null; notes?: string }) =>
      api.put<{ warning?: string }>(`/admin/connectors/${v.typeKey}/orgs/${v.orgId}`, {
        enabled: v.enabled,
        ...(v.notes ? { notes: v.notes } : {}),
      }),
    onSuccess: () => {
      setExceptionFor(null);
      setExceptionOrg("");
      setExceptionNotes("");
      refresh();
    },
  });

  const types = data?.types ?? [];
  const exceptions = data?.exceptions ?? [];
  const byCategory = ["intake", "outbound", "ats", "job_board"].map((cat) => ({
    cat,
    rows: types.filter((t) => t.category === cat),
  }));

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Connectors</h1>
          <div className="sub">Which integrations customers can have — and which customers</div>
        </div>
        {!isOwner && <span className="pill n">Read-only · owner writes</span>}
      </div>

      <div className="banner info" style={{ marginBottom: 18 }}>
        <Info />
        <div>
          Switching a type <b>on</b> offers it to every customer; switching it <b>off</b> withdraws
          it from everyone and stops it processing — but never deletes anybody's configuration or
          credentials. Per-organisation exceptions below override the default for one tenant. Each
          customer still connects or disconnects their own instance on their Connections page.
        </div>
      </div>

      {isLoading && <div className="card pad mut sm">Loading connector catalogue…</div>}
      {isError && (
        <div className="banner crit">
          <Alert />
          <div>{isForbidden ? "This screen needs platform access." : apiErrorText(error, "Couldn't load the connector catalogue.")}</div>
        </div>
      )}

      {byCategory.map(({ cat, rows }) =>
        rows.length === 0 ? null : (
          <div key={cat} style={{ marginBottom: 24 }}>
            <div style={{ fontWeight: 650, fontSize: 13, color: "var(--mut)", marginBottom: 8 }}>
              {CATEGORY_LABEL[cat] ?? cat}
            </div>
            <div className="card" style={{ overflow: "hidden" }}>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Who provides it</th>
                      <th>Auth</th>
                      <th>Adoption</th>
                      <th>Exceptions</th>
                      <th>Offered</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => (
                      <tr key={t.key}>
                        <td>
                          <div style={{ fontWeight: 600 }}>
                            {t.label}
                            {/* The honesty flag, front and centre in the console too: an
                                operator switching this on is offering a card, not a capability. */}
                            {!t.wired && (
                              <span className="pill n" style={{ marginLeft: 8 }}>
                                not live yet
                              </span>
                            )}
                          </div>
                          <div className="mut sm">{t.description}</div>
                        </td>
                        <td className="mut sm">
                          {t.provisioning === "native" ? (
                            <span className="pill t">TalentHop</span>
                          ) : (
                            <span className="pill n">Customer's own</span>
                          )}
                        </td>
                        <td className="mut sm">
                          {t.requiresAuth ? t.auth.replace(/_/g, " ") : "none"}
                          <div className="mut sm">
                            {t.fieldCount} field{t.fieldCount === 1 ? "" : "s"}
                            {t.secretFieldCount > 0 && ` · ${t.secretFieldCount} secret`}
                          </div>
                        </td>
                        <td className="mut sm">
                          {t.orgsConfigured} configured
                          <div className="mut sm">{t.orgsActive} active</div>
                        </td>
                        <td className="mut sm">{t.exceptions > 0 ? `${t.exceptions} org` : "—"}</td>
                        <td>
                          <Switch
                            on={t.enabled}
                            disabled={!isOwner || setAvailability.isPending}
                            onToggle={() => setAvailability.mutate({ key: t.key, enabled: !t.enabled })}
                          />
                        </td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <button className="btn sm ghost" onClick={() => setInspecting(t)}>
                            Fields
                          </button>
                          <button
                            className="btn sm ghost"
                            style={{ marginLeft: 6 }}
                            disabled={!isOwner}
                            onClick={() => {
                              setExceptionFor(t);
                              setExceptionEnabled(false);
                              setExceptionOrg("");
                              setExceptionNotes("");
                            }}
                          >
                            Per-org
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ),
      )}

      {/* ── the exception list ─────────────────────────────────────────────── */}
      <div style={{ fontWeight: 650, fontSize: 13, color: "var(--mut)", marginBottom: 8 }}>
        Per-organisation exceptions
      </div>
      <div className="card" style={{ overflow: "hidden", marginBottom: 24 }}>
        {exceptions.length === 0 ? (
          <div className="pad mut sm">
            No exceptions. Every organisation inherits the platform defaults above.
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Organisation</th>
                  <th>Connector</th>
                  <th>Decision</th>
                  <th>Reason</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {exceptions.map((e) => (
                  <tr key={`${e.orgId}:${e.typeKey}`}>
                    <td style={{ fontWeight: 600 }}>{e.orgName}</td>
                    <td className="mut sm">{e.typeLabel}</td>
                    <td>
                      {e.enabled ? (
                        <span className="pill v">granted</span>
                      ) : (
                        <span className="pill r">denied</span>
                      )}
                    </td>
                    {/* Internal only — the customer never sees this. */}
                    <td className="mut sm">{e.notes ?? "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="btn sm ghost"
                        disabled={!isOwner || setException.isPending}
                        onClick={() =>
                          setException.mutate({ typeKey: e.typeKey, orgId: e.orgId, enabled: null })
                        }
                        title="Clear the exception — this org returns to the platform default"
                      >
                        Reset to default
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── read-only field inspector ──────────────────────────────────────── */}
      {inspecting && (
        <Modal title={`${inspecting.label} · configuration fields`} onClose={() => setInspecting(null)}>
          <div className="mut sm" style={{ marginBottom: 12, lineHeight: 1.6 }}>
            These come from the shared connector registry and ship with the build — they're the
            same definitions the customer's form renders and the API validates against, so they
            can't drift. They aren't editable at runtime.
          </div>
          {inspecting.prerequisite && (
            <div className="banner info" style={{ marginBottom: 12 }}>
              <Info />
              <div>
                <b>Customer must provide:</b> {inspecting.prerequisite}
              </div>
            </div>
          )}
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Stored as</th>
                </tr>
              </thead>
              <tbody>
                {inspecting.fields.map((f) => (
                  <tr key={f.key}>
                    <td style={{ fontWeight: 600 }}>{f.label}</td>
                    <td className="mut sm">{f.type}</td>
                    <td className="mut sm">{f.required ? "yes" : "—"}</td>
                    <td className="mut sm">
                      {f.secret ? <span className="pill n">secret · never returned</span> : "config"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {/* ── per-org exception editor ───────────────────────────────────────── */}
      {exceptionFor && (
        <Modal
          title={`${exceptionFor.label} · one organisation`}
          onClose={() => setExceptionFor(null)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn ghost" onClick={() => setExceptionFor(null)}>
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={!exceptionOrg || setException.isPending}
                onClick={() =>
                  setException.mutate({
                    typeKey: exceptionFor.key,
                    orgId: exceptionOrg,
                    enabled: exceptionEnabled,
                    notes: exceptionNotes.trim() || undefined,
                  })
                }
              >
                {setException.isPending ? "Saving…" : "Save exception"}
              </button>
            </div>
          }
        >
          <div className="mut sm" style={{ marginBottom: 14, lineHeight: 1.6 }}>
            An exception overrides the platform default for one organisation only. Denying a type
            stops it processing for them however they've configured it, and they can't switch it
            back on.
          </div>

          {exceptionEnabled && !exceptionFor.enabled && (
            <div className="banner warn" style={{ marginBottom: 14 }}>
              <Alert />
              <div>
                {exceptionFor.label} is currently withdrawn platform-wide. Granting it to one
                organisation is saved, but takes effect only once the type itself is offered.
              </div>
            </div>
          )}

          <label style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 4 }} htmlFor="exc-org">
            Organisation
          </label>
          <select
            id="exc-org"
            className="input"
            value={exceptionOrg}
            onChange={(e) => setExceptionOrg(e.target.value)}
            style={{ width: "100%", marginBottom: 14 }}
          >
            <option value="">Select an organisation…</option>
            {(orgsData?.orgs ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>

          <label style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, cursor: "pointer" }}>
            <Switch on={exceptionEnabled} onToggle={() => setExceptionEnabled((v) => !v)} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>
              {exceptionEnabled ? "Granted to this organisation" : "Denied to this organisation"}
            </span>
          </label>

          <label style={{ display: "block", fontWeight: 600, fontSize: 13, marginBottom: 4 }} htmlFor="exc-notes">
            Reason (internal)
          </label>
          <input
            id="exc-notes"
            className="input"
            value={exceptionNotes}
            onChange={(e) => setExceptionNotes(e.target.value)}
            placeholder="e.g. pilot cohort, or paused pending incident #412"
            style={{ width: "100%" }}
          />
          <div style={{ fontSize: 12, color: "var(--mut)", marginTop: 4 }}>
            Shown in this console only — never to the customer.
          </div>

          {setException.isError && (
            <div className="banner crit" style={{ marginTop: 14 }}>
              <Alert />
              <div>{apiErrorText(setException.error, "Couldn't save that exception.")}</div>
            </div>
          )}
        </Modal>
      )}

      <div className="mut sm" style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Link2 />
        Customers manage their own instances at <b>/app/connections</b>.
      </div>
    </section>
  );
}
